import { useEffect, useState } from "react";
import api from "@/lib/api";
import { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Megaphone, X, Users, Info, AlertTriangle, CheckCircle2, FileText, RefreshCw, CircleDollarSign } from "lucide-react";
import { Link } from "react-router-dom";
import SubmissionsManager from "@/pages/admin/SubmissionsManager";
import FileUploadDropzone from "@/components/FileUploadDropzone";

const FIELD_LABELS = {
  player_name: "Player Name", parent_name: "Parent Name", age: "Age", dob: "Date of Birth",
  positions: "Positions", email: "Email", phone: "Phone", medical_notes: "Medical Notes",
  waiver_accepted: "Waiver Accepted", name: "Name", topic: "Topic", message: "Message",
  donor_name: "Donor Name", amount: "Amount", purpose: "Purpose", camp_id: "Camp",
  notes: "Notes", created_at: "Submitted At", id: "ID", status: "Status",
};

function formatFieldKey(k) {
  return FIELD_LABELS[k] || k.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatFieldValue(v) {
  if (v === true) return "Yes";
  if (v === false) return "No";
  if (v == null || v === "") return "—";
  if (typeof v === "string" && v.length > 20 && /^\d{4}-\d{2}-\d{2}T/.test(v)) {
    try { return new Date(v).toLocaleString(); } catch { return v; }
  }
  return String(v);
}

const TABS = [
  { k: "roster", l: "Roster", coll: "/roster", fields: [["name", "Name"], ["jersey_number", "#", "number"], ["position", "Position"], ["bats", "Bats"], ["throws", "Throws"], ["bio", "Bio", "textarea"]] },
  { k: "schedule", l: "Schedule", coll: "/schedule", fields: [["title", "Title"], ["type", "Type (practice/game/tournament/clinic/camp/service)"], ["date", "Date (ISO)"], ["location", "Location"], ["opponent", "Opponent"], ["notes", "Notes", "textarea"]] },
  { k: "scores", l: "Scores", coll: "/scores", fields: [["date", "Date"], ["opponent", "Opponent"], ["location", "Location"], ["runs_scored", "RS", "number"], ["runs_allowed", "RA", "number"], ["result", "Result (W/L/T)"], ["tournament", "Tournament"], ["placement", "Placement"]] },
  { k: "sponsors", l: "Sponsors", coll: "/sponsors", fields: [["name", "Name"], ["tier", "Tier (platinum/gold/silver/friends)"], ["amount", "Amount", "number"], ["website", "Website"], ["message", "Message"]] },
  { k: "camps", l: "Camps", coll: "/camps", fields: [["title", "Title"], ["type", "Type (camp/clinic)"], ["date", "Date"], ["location", "Location"], ["price", "Price", "number"], ["capacity", "Capacity", "number"], ["description", "Description", "textarea"]] },
  { k: "merch", l: "Merch", coll: "/merch", fields: [["name", "Name"], ["category", "Category"], ["price", "Price", "number"], ["image_url", "Image URL"], ["description", "Description", "textarea"]] },
  { k: "news", l: "News", coll: "/news", fields: [["title", "Title"], ["body", "Body", "textarea"], ["author", "Author"], ["image_url", "Image URL"], ["published_at", "Date"]] },
  { k: "media", l: "Media", coll: "/media", fields: [["title", "Title"], ["type", "Type (photo/video)"], ["url", "URL"], ["album", "Album"], ["caption", "Caption"]] },
  { k: "announcements", l: "Announcements", coll: "/announcements", fields: [["title", "Title"], ["body", "Body", "textarea"], ["audience", "Audience"], ["pinned", "Pinned (true/false)"]] },
  { k: "documents", l: "Documents", coll: "/documents", fields: [["title", "Title"], ["category", "Category (promise/permission/medical/waiver/general)"], ["description", "Description", "textarea"], ["body", "Body / instructions (optional)", "textarea"], ["requires_signature", "Requires Sig (true/false)"], ["required", "Required (true/false)"], ["file_url", "Uploaded file URL (auto-fills after upload)"]] },
  { k: "faq", l: "FAQ", coll: "/faq", fields: [["question", "Question"], ["answer", "Answer", "textarea"], ["category", "Category"]] },
];

function castVal(field, type, val) {
  if (type === "number") return val === "" ? 0 : Number(val);
  // Boolean-style fields (label hints like "(true/false)") — treat empty as false
  if (typeof val === "string") {
    const v = val.trim().toLowerCase();
    if (v === "true") return true;
    if (v === "false") return false;
  }
  return val;
}

function singularize(label) {
  // Simple singularize: handle common plural endings cleanly
  if (/ies$/i.test(label)) return label.replace(/ies$/i, "y");
  if (/sses$/i.test(label)) return label.replace(/es$/i, "");
  if (/[^s]s$/i.test(label)) return label.slice(0, -1);
  return label; // e.g. "Roster", "Schedule", "News"
}

export default function Admin() {
  const [tab, setTab] = useState(TABS[0]);
  const [items, setItems] = useState([]);
  const [draft, setDraft] = useState({});
  const [detail, setDetail] = useState(null); // { title, item }
  const [notifyEmail, setNotifyEmail] = useState(true);
  const [deliveryModal, setDeliveryModal] = useState(null);
  const [emailConfig, setEmailConfig] = useState(null);
  const [replaceFor, setReplaceFor] = useState(null); // document item being replaced

  const load = () => api.get(tab.coll).then((r) => setItems(r.data));
  useEffect(() => {
    load();
    setDraft({});
    setNotifyEmail(true);
    /* eslint-disable-next-line */
  }, [tab]);

  useEffect(() => {
    if (tab.k === "announcements") {
      api.get("/admin/email-config").then((r) => setEmailConfig(r.data)).catch(() => {});
    }
  }, [tab]);

  const create = async (e) => {
    e.preventDefault();
    const payload = {};
    tab.fields.forEach(([k, label, type]) => {
      const raw = draft[k] ?? "";
      // For boolean-style fields (label hints "(true/false)"), default empty → false
      if (typeof label === "string" && /\btrue\s*\/\s*false\b/i.test(label) && raw === "") {
        payload[k] = false;
        return;
      }
      // Skip empty string values for optional text/textarea — let backend defaults apply
      if (type !== "number" && raw === "") return;
      payload[k] = castVal(k, type, raw);
    });
    if (tab.k === "announcements") payload.notify_email = notifyEmail;
    try {
      const res = await api.post(tab.coll, payload);
      const stats = res.data?.email_stats;
      if (stats) {
        const failed = Number(stats.failed) || 0;
        const sent = Number(stats.sent) || 0;
        const mockNote = stats.mocked ? " (mock mode — RESEND_API_KEY is not set)" : "";
        const attempted = Number(stats.attempted ?? stats.recipients) || 0;
        if (attempted === 0) {
          toast.message("Announcement posted. No valid user emails found — nothing was sent.");
        } else if (failed > 0) {
          toast.error(
            `Announcement posted. ${sent}/${attempted} emails sent · ${failed} failed.${mockNote} Click 'Delivery Details' to see why.`,
            { duration: 8000 }
          );
        } else {
          toast.success(`Announcement posted. Emails sent to ${sent} users.${mockNote}`);
        }
      } else if (tab.k === "announcements") {
        toast.success("Announcement posted (email broadcast was skipped).");
      } else {
        toast.success("Added");
      }
      setDraft({});
      setNotifyEmail(true);
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed"); }
  };
  const del = async (id) => {
    if (!confirm("Delete?")) return;
    await api.delete(`${tab.coll}/${id}`); load();
  };
  const notify = async (id) => {
    try {
      const { data } = await api.post(`/announcements/${id}/notify`);
      const s = data.stats || {};
      const failed = Number(s.failed) || 0;
      const sent = Number(s.sent) || 0;
      const mockNote = s.mocked ? " (mock mode)" : "";
      if (failed > 0) {
        toast.error(`Re-sent ${sent}, ${failed} failed.${mockNote} See Delivery Details.`, { duration: 8000 });
      } else {
        toast.success(`Re-sent to ${sent} users${mockNote}.`);
      }
      load();
    } catch { toast.error("Failed"); }
  };

  return (
    <div data-testid="admin-page" className="min-w-0">
      <div className="eyebrow mb-1">Admin Console</div>
      <h1 className="font-display text-2xl sm:text-3xl uppercase mb-5">Manage Everything</h1>

      <div className="flex flex-wrap gap-1.5 pb-2 mb-5 border-b border-white/10">
        {TABS.map((t) => (
          <button key={t.k} onClick={() => setTab(t)} className={`px-3 py-1.5 rounded-md text-xs sm:text-sm ${tab.k === t.k ? "bg-[#00E5FF]/10 text-[#00E5FF]" : "text-slate-300 hover:text-white"}`} data-testid={`admin-tab-${t.k}`}>
            {t.l}
          </button>
        ))}
        <button onClick={() => setTab({ k: "subs", l: "Submissions" })} className={`px-3 py-1.5 rounded-md text-xs sm:text-sm ${tab.k === "subs" ? "bg-[#00E5FF]/10 text-[#00E5FF]" : "text-slate-300 hover:text-white"}`} data-testid="admin-tab-subs">
          Submissions
        </button>
        <Link
          to="/admin/accounts"
          className="px-3 py-1.5 rounded-md text-xs sm:text-sm text-slate-300 hover:text-white inline-flex items-center gap-1.5"
          data-testid="admin-tab-accounts"
        >
          <Users size={12} /> Accounts
        </Link>
        <Link
          to="/admin/payments"
          className="px-3 py-1.5 rounded-md text-xs sm:text-sm text-slate-300 hover:text-white inline-flex items-center gap-1.5"
          data-testid="admin-tab-payments"
        >
          <CircleDollarSign size={12} /> Payments
        </Link>
      </div>

      {tab.k === "announcements" && emailConfig && (
        <div className={`mb-4 rounded-xl border p-4 text-sm ${
          emailConfig.ready
            ? "bg-[#00E5FF]/5 border-[#00E5FF]/30 text-slate-200"
            : "bg-yellow-500/5 border-yellow-400/40 text-yellow-200"
        }`} data-testid="email-config-banner">
          <div className="flex items-start gap-2 flex-wrap">
            <Info size={14} className="mt-0.5 shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="font-semibold">Email Service Status</div>
              <div className="text-xs mt-1 break-words">
                {emailConfig.ready
                  ? <>Connected. Sending from <code className="text-[#00E5FF]">{emailConfig.resend_from}</code>{emailConfig.announcement_reply_to ? <> · replies go to <code>{emailConfig.announcement_reply_to}</code></> : null}.{emailConfig.sandbox_mode ? <span className="block mt-1 text-yellow-300">⚠ Sandbox mode — onboarding@resend.dev only delivers to the email that owns the Resend account. Verify a domain in Resend to send to all users.</span> : null}</>
                  : "RESEND_API_KEY or RESEND_FROM not configured. Announcements will still post, but no emails will be sent."}
              </div>
            </div>
          </div>
        </div>
      )}

      {tab.k === "subs" ? (
        <SubmissionsManager />
      ) : (
        <div className="grid lg:grid-cols-[1fr_360px] gap-5 min-w-0">
          <div className="space-y-2 min-w-0">
            {items.map((it) => (
              <div key={it.id} className="kr-card rounded-xl p-4 flex items-start justify-between gap-3" data-testid={`admin-row-${it.id}`}>
                <div className="flex-1 min-w-0">
                  {tab.fields.slice(0, 4).map(([k]) => (
                    <div key={k} className="text-sm truncate"><span className="text-slate-500 text-xs">{k}:</span> {String(it[k] ?? "—")}</div>
                  ))}
                  {tab.k === "documents" && (
                    <div className="mt-2 flex flex-wrap items-center gap-2">
                      {it.file_url ? (
                        <a
                          href={it.file_url}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-1.5 text-[11px] text-[#00E5FF] hover:text-white bg-[#00E5FF]/10 border border-[#00E5FF]/30 rounded-full px-2.5 py-1"
                          data-testid={`doc-file-link-${it.id}`}
                        >
                          <FileText size={11} /> {it.file_name || "Attached file"}
                        </a>
                      ) : (
                        <span className="text-[11px] text-slate-500 inline-flex items-center gap-1.5">
                          <FileText size={11} /> No file attached
                        </span>
                      )}
                      <button
                        type="button"
                        onClick={() => setReplaceFor(it)}
                        className="text-[11px] uppercase tracking-widest text-slate-300 hover:text-[#00E5FF] inline-flex items-center gap-1"
                        data-testid={`doc-replace-${it.id}`}
                      >
                        <RefreshCw size={10} /> {it.file_url ? "Replace" : "Upload"}
                      </button>
                    </div>
                  )}
                  {tab.k === "announcements" && it.email_stats && (
                    <div className="mt-2 flex flex-wrap items-center gap-2">
                      <span className={`text-[10px] uppercase tracking-widest inline-flex items-center gap-1.5 rounded-full px-2 py-1 ${
                        it.email_stats.failed > 0
                          ? "text-red-300 bg-red-500/10 border border-red-400/30"
                          : "text-[#00E5FF] bg-[#00E5FF]/10 border border-[#00E5FF]/30"
                      }`}>
                        {it.email_stats.failed > 0
                          ? <AlertTriangle size={10} />
                          : <CheckCircle2 size={10} />}
                        Sent {it.email_stats.sent}/{it.email_stats.attempted ?? it.email_stats.recipients}
                        {it.email_stats.failed > 0 ? ` · ${it.email_stats.failed} failed` : ""}
                        {it.email_stats.mocked ? " · mock" : ""}
                      </span>
                      {(it.email_stats.failed > 0 || (it.email_stats.failed_details && it.email_stats.failed_details.length > 0)) && (
                        <button
                          type="button"
                          onClick={() => setDeliveryModal(it)}
                          className="text-[10px] uppercase tracking-widest text-yellow-300 hover:text-yellow-200 underline underline-offset-2"
                          data-testid={`delivery-details-${it.id}`}
                        >
                          Delivery Details
                        </button>
                      )}
                    </div>
                  )}
                </div>
                <div className="flex flex-col gap-1 shrink-0">
                  {tab.k === "announcements" && <button onClick={() => notify(it.id)} className="btn-ghost text-xs"><Megaphone size={12} /> Resend</button>}
                  <button onClick={() => del(it.id)} className="text-red-400 text-xs hover:bg-red-500/10 p-2 rounded"><Trash2 size={14} /></button>
                </div>
              </div>
            ))}
            {items.length === 0 && <p className="text-slate-500">Nothing yet.</p>}
          </div>
          <form onSubmit={create} className="kr-card rounded-xl p-5 space-y-3 h-fit min-w-0" data-testid="admin-create-form">
            <div className="eyebrow flex items-center gap-2"><Plus size={14} /> Add {singularize(tab.l)}</div>
            {tab.fields.map(([k, label, type = "text"]) => {
              // For Documents tab, replace the manual file_url text input with the dropzone (rendered below)
              if (tab.k === "documents" && k === "file_url") return null;
              return type === "textarea" ? (
                <textarea key={k} rows={3} placeholder={label} value={draft[k] || ""} onChange={(e) => setDraft({ ...draft, [k]: e.target.value })} className="w-full bg-[#050A15] border border-white/10 rounded-md p-2 text-sm" />
              ) : (
                <input key={k} type={type === "number" ? "number" : "text"} placeholder={label} value={draft[k] || ""} onChange={(e) => setDraft({ ...draft, [k]: e.target.value })} className="w-full bg-[#050A15] border border-white/10 rounded-md p-2 text-sm" />
              );
            })}
            {tab.k === "documents" && (
              <div className="space-y-1.5">
                <div className="text-[11px] uppercase tracking-widest text-[#00E5FF]">Attach File (optional)</div>
                <FileUploadDropzone
                  key={items.length}
                  folder="documents"
                  currentUrl={draft.file_url || ""}
                  currentName={draft.file_name || ""}
                  onUploaded={({ url, original_filename }) => setDraft({ ...draft, file_url: url, file_name: original_filename })}
                  onCleared={() => setDraft({ ...draft, file_url: "", file_name: "" })}
                  testId="admin-doc-dropzone"
                />
              </div>
            )}
            {tab.k === "announcements" && (
              <label className="flex items-start gap-2 text-sm cursor-pointer p-2 -m-2 rounded hover:bg-white/5" data-testid="notify-email-toggle">
                <input
                  type="checkbox"
                  checked={notifyEmail}
                  onChange={(e) => setNotifyEmail(e.target.checked)}
                  className="mt-1 shrink-0"
                />
                <div className="min-w-0">
                  <div className="text-white">Email all users</div>
                  <div className="text-[11px] text-slate-400 leading-snug">
                    Emails will be sent to active users with valid email addresses. Disabled users are skipped.
                  </div>
                </div>
              </label>
            )}
            <button className="btn-primary w-full justify-center">Create</button>
          </form>
        </div>
      )}

      {/* Submission detail modal */}
      {detail && (
        <div
          className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-start sm:items-center justify-center p-4 overflow-y-auto"
          onClick={() => setDetail(null)}
          data-testid="submission-detail-modal"
        >
          <div onClick={(e) => e.stopPropagation()} className="kr-card rounded-2xl p-6 sm:p-8 max-w-xl w-full my-8 relative">
            <button
              onClick={() => setDetail(null)}
              className="absolute top-4 right-4 text-slate-400 hover:text-white"
              aria-label="Close"
              data-testid="submission-detail-close"
            >
              <X size={20} />
            </button>
            <div className="eyebrow mb-1">{detail.title}</div>
            <h2 className="font-display text-2xl uppercase mb-6">Submission Details</h2>
            <dl className="space-y-3">
              {Object.entries(detail.item)
                .filter(([k]) => k !== "_id")
                .map(([k, v]) => (
                  <div key={k} className="grid sm:grid-cols-[160px_1fr] gap-1 sm:gap-4 py-2 border-b border-white/10 last:border-0">
                    <dt className="text-xs uppercase tracking-widest text-[#00E5FF]">{formatFieldKey(k)}</dt>
                    <dd className="text-sm text-slate-200 break-words whitespace-pre-wrap">{formatFieldValue(v)}</dd>
                  </div>
                ))}
            </dl>
            <div className="mt-6 flex flex-wrap gap-2">
              {detail.item.email && (
                <a href={`mailto:${detail.item.email}`} className="btn-primary text-sm" data-testid="submission-email">
                  Reply by Email
                </a>
              )}
              {detail.item.phone && (
                <a href={`tel:${detail.item.phone}`} className="btn-ghost text-sm" data-testid="submission-phone">
                  Call {detail.item.phone}
                </a>
              )}
              <button
                onClick={() => {
                  navigator.clipboard.writeText(JSON.stringify(detail.item, null, 2));
                  toast.success("Copied to clipboard");
                }}
                className="btn-ghost text-sm"
                data-testid="submission-copy"
              >
                Copy All
              </button>
            </div>
          </div>
        </div>
      )}

      {/* DELIVERY DETAILS MODAL */}
      {deliveryModal && (
        <div
          className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm overflow-y-auto"
          onClick={() => setDeliveryModal(null)}
          data-testid="delivery-details-modal"
        >
          <div className="min-h-full flex items-start sm:items-center justify-center p-4">
            <div onClick={(e) => e.stopPropagation()} className="kr-card rounded-2xl p-6 sm:p-8 max-w-2xl w-full my-8 relative">
              <button onClick={() => setDeliveryModal(null)} className="absolute top-4 right-4 text-slate-400 hover:text-white">
                <X size={20} />
              </button>
              <div className="eyebrow mb-1">Email Delivery Details</div>
              <h2 className="font-display text-xl sm:text-2xl uppercase mb-1 break-words">{deliveryModal.title}</h2>
              <p className="text-xs text-slate-400 mb-5">
                Last sent {deliveryModal.email_stats?.last_sent_at ? new Date(deliveryModal.email_stats.last_sent_at).toLocaleString() : "—"}
              </p>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-5">
                {[
                  ["Attempted", deliveryModal.email_stats?.attempted ?? deliveryModal.email_stats?.recipients ?? 0, "text-white"],
                  ["Sent", deliveryModal.email_stats?.sent ?? 0, "text-green-300"],
                  ["Failed", deliveryModal.email_stats?.failed ?? 0, "text-red-300"],
                  ["Skipped", Object.values(deliveryModal.email_stats?.skipped || {}).reduce((s, v) => s + (Number(v) || 0), 0), "text-yellow-300"],
                ].map(([l, v, c]) => (
                  <div key={l} className="bg-[#050A15] rounded-md p-3">
                    <div className="text-[10px] uppercase tracking-widest text-slate-400">{l}</div>
                    <div className={`font-display text-2xl ${c}`}>{v}</div>
                  </div>
                ))}
              </div>

              {deliveryModal.email_stats?.sender && (
                <div className="text-xs text-slate-400 mb-4 space-y-1">
                  <div>From: <code className="text-slate-200">{deliveryModal.email_stats.sender}</code></div>
                  {deliveryModal.email_stats.reply_to && (
                    <div>Reply-to: <code className="text-slate-200">{deliveryModal.email_stats.reply_to}</code></div>
                  )}
                  {deliveryModal.email_stats.mocked && (
                    <div className="text-yellow-300 mt-1">⚠ Mock mode — no real emails were sent.</div>
                  )}
                </div>
              )}

              {deliveryModal.email_stats?.skipped && Object.values(deliveryModal.email_stats.skipped).some((v) => v > 0) && (
                <div className="mb-4 text-xs text-slate-300 space-y-1">
                  <div className="eyebrow text-[10px] mb-1">Skipped users</div>
                  {deliveryModal.email_stats.skipped.disabled > 0 && <div>· {deliveryModal.email_stats.skipped.disabled} disabled account(s)</div>}
                  {deliveryModal.email_stats.skipped.no_email > 0 && <div>· {deliveryModal.email_stats.skipped.no_email} user(s) without an email address</div>}
                  {deliveryModal.email_stats.skipped.invalid_email > 0 && <div>· {deliveryModal.email_stats.skipped.invalid_email} user(s) with invalid email format</div>}
                  {deliveryModal.email_stats.skipped.duplicates > 0 && <div>· {deliveryModal.email_stats.skipped.duplicates} duplicate email(s)</div>}
                </div>
              )}

              {deliveryModal.email_stats?.failed_details && deliveryModal.email_stats.failed_details.length > 0 ? (
                <div>
                  <div className="eyebrow text-[10px] mb-2">Failures</div>
                  <div className="space-y-2 max-h-72 overflow-y-auto">
                    {deliveryModal.email_stats.failed_details.map((f, i) => (
                      <div key={i} className="bg-red-500/5 border border-red-400/30 rounded-md p-3" data-testid={`failure-row-${i}`}>
                        <div className="flex items-start justify-between gap-2 flex-wrap mb-1">
                          <div className="text-sm font-semibold text-red-200 break-all">{f.email}</div>
                          <div className="text-[10px] text-slate-400 whitespace-nowrap">{f.timestamp ? new Date(f.timestamp).toLocaleString() : ""}</div>
                        </div>
                        <div className="text-sm text-slate-200 break-words">{f.reason}</div>
                        {f.raw_error && f.raw_error !== f.reason && (
                          <details className="mt-1">
                            <summary className="text-[10px] uppercase tracking-widest text-slate-500 cursor-pointer">Raw error</summary>
                            <pre className="text-[10px] text-slate-400 whitespace-pre-wrap break-words mt-1">{f.raw_error}</pre>
                          </details>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="text-sm text-slate-400">No failed deliveries on the last send.</div>
              )}

              {deliveryModal.email_stats?.note && (
                <div className="mt-4 text-sm text-yellow-300">{deliveryModal.email_stats.note}</div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* REPLACE / UPLOAD FILE MODAL (Documents tab) */}
      {replaceFor && (
        <div
          className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-start sm:items-center justify-center p-4 overflow-y-auto"
          onClick={() => setReplaceFor(null)}
          data-testid="doc-replace-modal"
        >
          <div onClick={(e) => e.stopPropagation()} className="kr-card rounded-2xl p-6 sm:p-8 max-w-lg w-full my-8 relative">
            <button onClick={() => setReplaceFor(null)} className="absolute top-4 right-4 text-slate-400 hover:text-white" aria-label="Close" data-testid="doc-replace-close">
              <X size={20} />
            </button>
            <div className="eyebrow mb-1">Document File</div>
            <h2 className="font-display text-2xl uppercase mb-1">{replaceFor.title}</h2>
            <p className="text-xs text-slate-400 mb-5">
              Upload a PDF or DOC for parents to download. Replacing a file does not affect existing signatures.
            </p>
            <FileUploadDropzone
              folder="documents"
              currentUrl={replaceFor.file_url || ""}
              currentName={replaceFor.file_name || ""}
              label={replaceFor.file_url ? "Drop a replacement file" : "Drop file to attach"}
              onUploaded={async ({ url, original_filename }) => {
                try {
                  const payload = {
                    title: replaceFor.title,
                    category: replaceFor.category || "general",
                    description: replaceFor.description || "",
                    body: replaceFor.body || "",
                    file_url: url,
                    file_name: original_filename || "",
                    requires_signature: !!replaceFor.requires_signature,
                    requires_co_signer: !!replaceFor.requires_co_signer,
                    required: !!replaceFor.required,
                  };
                  await api.put(`/documents/${replaceFor.id}`, payload);
                  toast.success("File saved to document");
                  setReplaceFor(null);
                  load();
                } catch (err) {
                  toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to save file to document");
                }
              }}
              onCleared={async () => {
                if (!replaceFor.file_url) return;
                try {
                  await api.put(`/documents/${replaceFor.id}`, {
                    title: replaceFor.title,
                    category: replaceFor.category || "general",
                    description: replaceFor.description || "",
                    body: replaceFor.body || "",
                    file_url: "",
                    file_name: "",
                    requires_signature: !!replaceFor.requires_signature,
                    requires_co_signer: !!replaceFor.requires_co_signer,
                    required: !!replaceFor.required,
                  });
                  toast.success("File removed from document");
                  setReplaceFor(null);
                  load();
                } catch (err) {
                  toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to remove file");
                }
              }}
              testId="admin-doc-replace-dropzone"
            />
          </div>
        </div>
      )}
    </div>
  );
}
